import React from 'react';
import { Box, Button, Typography, Paper } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { Rule, SimpleRule, ComplexRule, RuleColumn } from '../../types/leadSourceRules';
import { RuleRow } from './RuleRow';
import { RuleGroup } from './RuleGroup';

interface RuleBuilderProps {
  rules: Rule[];
  columns: RuleColumn[];
  onChange: (rules: Rule[]) => void;
}

export const RuleBuilder: React.FC<RuleBuilderProps> = ({ rules, columns, onChange }) => {
  // Create a new simple rule
  const createSimpleRule = (): SimpleRule => ({
    type: 'simple',
    column: columns[0]?.column_name || 'origin_host',
    operator: '=',
    value: '',
  });

  // Create a new complex rule (group)
  const createComplexRule = (operator: 'AND' | 'OR' = 'AND'): ComplexRule => ({
    type: 'complex',
    operator,
    rules: [createSimpleRule()],
  });

  // Add a new simple rule at root level
  const addRule = () => {
    onChange([...rules, createSimpleRule()]);
  };

  // Add a new group at root level
  const addGroup = (operator: 'AND' | 'OR') => {
    onChange([...rules, createComplexRule(operator)]);
  };

  // Update a rule at index
  const updateRule = (index: number, updatedRule: Rule) => {
    const newRules = [...rules];
    newRules[index] = updatedRule;
    onChange(newRules);
  };

  // Delete a rule at index
  const deleteRule = (index: number) => {
    onChange(rules.filter((_, i) => i !== index));
  };

  // Move a rule up or down
  const moveRule = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= rules.length) return;
    const newRules = [...rules];
    [newRules[index], newRules[newIndex]] = [newRules[newIndex], newRules[index]];
    onChange(newRules);
  };

  // Copy a rule
  const copyRule = (index: number) => {
    const ruleCopy = JSON.parse(JSON.stringify(rules[index]));
    const newRules = [...rules];
    newRules.splice(index + 1, 0, ruleCopy);
    onChange(newRules);
  };

  const renderRule = (rule: Rule, index: number) => {
    if (rule.type === 'simple') {
      return (
        <RuleRow
          key={index}
          rule={rule}
          columns={columns}
          index={index}
          totalRules={rules.length}
          onChange={(updated) => updateRule(index, updated)}
          onDelete={() => deleteRule(index)}
          onMoveUp={() => moveRule(index, 'up')}
          onMoveDown={() => moveRule(index, 'down')}
          onCopy={() => copyRule(index)}
        />
      );
    } else {
      return (
        <RuleGroup
          key={index}
          rule={rule}
          columns={columns}
          index={index}
          totalRules={rules.length}
          onChange={(updated) => updateRule(index, updated)}
          onDelete={() => deleteRule(index)}
          onMoveUp={() => moveRule(index, 'up')}
          onMoveDown={() => moveRule(index, 'down')}
          onCopy={() => copyRule(index)}
        />
      );
    }
  };

  return (
    <Box>
      {rules.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: '#f5f5f5',
            borderStyle: 'dashed',
          }}
        >
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            No rules defined for this source.
          </Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addRule}>
            Add First Rule
          </Button>
        </Paper>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {rules.map((rule, index) => (
            <Box key={index}>
              {index > 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: 'center', py: 0.5, fontWeight: 'bold' }}
                >
                  AND
                </Typography>
              )}
              {renderRule(rule, index)}
            </Box>
          ))}
        </Box>
      )}

      {rules.length > 0 && (
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button size="small" startIcon={<AddIcon />} onClick={addRule}>
            Add Rule
          </Button>
          <Button size="small" variant="outlined" onClick={() => addGroup('AND')}>
            Add AND Group
          </Button>
          <Button size="small" variant="outlined" onClick={() => addGroup('OR')}>
            Add OR Group
          </Button>
        </Box>
      )}
    </Box>
  );
};
